
import { useState, useEffect, useMemo } from "react";
import { ExpenseItem } from "./ExpenseItem";
import { ExpenseForm } from "./ExpenseForm";
import { Expense } from "@/types/expense";
import { Member } from "@/types/member";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { PlusCircle, RefreshCw, Camera } from "lucide-react";
import { ScanReceiptButton } from "./ScanReceiptButton";
import { useSupabaseAuth } from "@/hooks/auth";

interface ExpensesSectionProps {
  expenses: Expense[];
  members: Member[];
  currencies: {
    code: string;
    symbol: string;
  }[];
  groupId: string;
  showExpenseForm: boolean;
  editingExpenseId: string | null;
  expenseFormState: {
    title: string;
    amount: string;
    currency: string;
    paidBy: string;
    sharedWith: string[];
  };
  onAddClick?: () => void;
  onCancel: () => void;
  onSubmit: (formData: {
    title: string;
    amount: string;
    currency: string;
    paidBy: string;
    sharedWith: string[];
  }) => Promise<void>;
  onEditExpense: (expense: Expense) => void;
  onDeleteExpense?: (expenseId: string) => Promise<void>;
  onRefresh?: () => Promise<void>;
  onScanReceipt?: () => void;
}

export const ExpensesSection = ({
  expenses,
  members,
  currencies,
  groupId,
  showExpenseForm,
  editingExpenseId,
  expenseFormState,
  onAddClick,
  onCancel,
  onSubmit,
  onEditExpense,
  onDeleteExpense,
  onRefresh,
  onScanReceipt
}: ExpensesSectionProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const { user } = useSupabaseAuth();

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoadComplete(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (expenses.length > 0) {
      setInitialLoadComplete(true);
    }
  }, [expenses]);

  // Sort expenses to show the ones the user is part of first
  const sortedExpenses = useMemo(() => {
    if (!user) return expenses;
    
    return [...expenses].sort((a, b) => {
      // Check if current user is part of expense a
      const userInA = a.sharedWith.includes(user.id) || a.paidBy === user.id;
      // Check if current user is part of expense b
      const userInB = b.sharedWith.includes(user.id) || b.paidBy === user.id;
      
      if (userInA && !userInB) return -1; // User is in A but not B, so A comes first
      if (!userInA && userInB) return 1;  // User is in B but not A, so B comes first
      
      // If user is in both or in neither, sort by date (newest first)
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [expenses, user]);

  const handleRefresh = async () => {
    if (!onRefresh) return;
    setIsLoading(true);
    try {
      await onRefresh();
    } catch (error) {
      console.error("Error refreshing expenses:", error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('expenses.title')}</h2>
        {onRefresh && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
            className="h-8 w-8 p-0"
            aria-label={t('common.refresh')}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        )}
      </div>

      <div className="flex flex-col space-y-2">
        <Button 
          onClick={onAddClick} 
          className="w-full bg-gradient-to-r from-primary to-green text-white"
        >
          <PlusCircle className="mr-2 h-4 w-4" />
          {t('expenses.addExpense')}
        </Button>
        
        <ScanReceiptButton onClick={onScanReceipt || (() => {})} />
      </div>

      {showExpenseForm && (
        <ExpenseForm 
          groupId={groupId} 
          members={members} 
          currencies={currencies} 
          showExpenseForm={showExpenseForm} 
          editingExpenseId={editingExpenseId} 
          initialExpense={expenseFormState} 
          onCancel={onCancel} 
          onSubmit={onSubmit} 
        />
      )}

      <div className="space-y-2">
        {!initialLoadComplete ? (
          <div className="p-4 rounded-lg bg-gray-100 dark:bg-gray-800 text-center">
            <div className="flex flex-col items-center justify-center space-y-2">
              <RefreshCw className="h-5 w-5 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            </div>
          </div>
        ) : sortedExpenses.length > 0 ? (
          sortedExpenses.map(expense => {
            // Check if current user is part of this expense
            const userInExpense = user && (expense.sharedWith.includes(user.id) || expense.paidBy === user.id);
            
            return (
              <ExpenseItem 
                key={expense.id} 
                expense={expense} 
                currencies={currencies} 
                onEditExpense={onEditExpense} 
                onDeleteExpense={onDeleteExpense}
                highlighted={userInExpense}
              />
            );
          })
        ) : (
          <div className="p-4 rounded-lg bg-gray-100 dark:bg-gray-800 text-center">
            <p className="text-sm text-muted-foreground">{t('expenses.noExpensesYet')}</p>
            <p className="text-xs text-muted-foreground mt-1">{t('expenses.addExpensePrompt')}</p>
          </div>
        )}
      </div>
    </div>
  );
};
